/* Store locator ------------------------------------------------------------ */

(function () {
  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  /* Time helpers ----------------------------------------------------------- */

  function nowIn(timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    const get = (type) => parts.find((p) => p.type === type).value;
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    const hour = parseInt(get('hour'), 10) % 24;

    return { day, minutes: hour * 60 + parseInt(get('minute'), 10) };
  }

  function toMinutes(value) {
    const [h, m] = value.split(':').map(Number);
    return h * 60 + m;
  }

  function ranges(value) {
    if (!value || /closed/i.test(value)) return [];
    return value.split(',').map((chunk) => {
      const [from, to] = chunk.trim().split('-');
      if (!from || !to) return null;
      return { from: toMinutes(from), to: toMinutes(to) };
    }).filter(Boolean);
  }

  function clock(minutes) {
    const h = Math.floor(minutes / 60);
    const m = String(minutes % 60).padStart(2, '0');
    const suffix = h >= 12 ? 'pm' : 'am';
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display}:${m} ${suffix}`;
  }

  function status(store, timezone) {
    const now = nowIn(timezone);
    const today = ranges(store.hours[DAYS[now.day]]);
    const current = today.find((r) => now.minutes >= r.from && now.minutes < r.to);

    if (current) {
      const soon = current.to - now.minutes <= 60;
      return {
        open: true,
        soon: soon,
        label: soon ? `Closing at ${clock(current.to)}` : `Open until ${clock(current.to)}`
      };
    }

    const later = today.find((r) => r.from > now.minutes);
    if (later) return { open: false, label: `Opens today ${clock(later.from)}` };

    for (let i = 1; i <= 7; i++) {
      const index = (now.day + i) % 7;
      const next = ranges(store.hours[DAYS[index]])[0];
      if (next) {
        const when = i === 1 ? 'tomorrow' : LABELS[index];
        return { open: false, label: `Opens ${when} ${clock(next.from)}` };
      }
    }

    return { open: false, label: 'Temporarily closed' };
  }

  function hoursText(value) {
    const list = ranges(value);
    if (!list.length) return 'Closed';
    return list.map((r) => `${clock(r.from)} – ${clock(r.to)}`).join(', ');
  }

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  /* Geo -------------------------------------------------------------------- */

  function distanceKm(a, b) {
    const rad = (deg) => (deg * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(h));
  }

  function pinMarkup(label) {
    return `<svg viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Store ${esc(label)}">
      <ellipse class="pin__shadow" cx="17" cy="43" rx="6" ry="2.2"/>
      <path class="pin__body" d="M17 1.5c8.3 0 15 6.5 15 14.6C32 26.4 17 41 17 41S2 26.4 2 16.1C2 8 8.7 1.5 17 1.5z"/>
      <circle class="pin__disc" cx="17" cy="16" r="9.6"/>
      <g class="pin__icon">
        <rect x="11.4" y="10.6" width="11.2" height="2.4" rx="0.5"/>
        <path class="pin__shop" d="M12.6 14.4h8.8v6.8h-8.8z"/>
      </g>
    </svg>`;
  }

  /* Element ---------------------------------------------------------------- */

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.timezone = this.dataset.timezone || 'Australia/Adelaide';
      this.stores = JSON.parse(this.querySelector('[data-locator-stores]').textContent)
        .filter((s) => s.lat && s.lng);

      this.listEl = this.querySelector('[data-locator-list]');
      this.emptyEl = this.querySelector('[data-locator-empty]');
      this.countEl = this.querySelector('[data-locator-count]');
      this.detailEl = this.querySelector('[data-locator-detail]');
      this.searchEl = this.querySelector('[data-locator-search]');

      this.visible = this.stores.slice();
      this.markers = new Map();
      this.activeId = null;

      this.label();
      this.renderList();
      this.bind();
      this.initMap();
    }

    label() {
      this.stores.forEach((store, index) => {
        store.label = index < 26 ? ALPHABET[index] : String(index + 1);
      });
    }

    bind() {
      this.searchEl.addEventListener('input', () => this.filter(this.searchEl.value));

      this.querySelector('[data-locator-nearby]').addEventListener('click', (event) => {
        this.locate(event.currentTarget);
      });

      this.querySelectorAll('[data-locator-view]').forEach((button) => {
        button.addEventListener('click', () => {
          this.dataset.view = button.dataset.locatorView;
          this.querySelectorAll('[data-locator-view]').forEach((b) => b.classList.remove('is-active'));
          button.classList.add('is-active');
          if (this.map) google.maps.event.trigger(this.map, 'resize');
        });
      });

      this.listEl.addEventListener('click', (event) => {
        const row = event.target.closest('[data-store-id]');
        if (row) this.select(row.dataset.storeId, { pan: true });
      });

      this.detailEl.addEventListener('click', (event) => {
        if (event.target.closest('[data-detail-close]')) this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.activeId) this.close();
      });
    }

    /* List ----------------------------------------------------------------- */

    renderList() {
      this.listEl.innerHTML = this.visible.map((store) => {
        const state = status(store, this.timezone);
        const place = [store.suburb, store.postcode].filter(Boolean).join(' ');
        const tone = state.open ? (state.soon ? 'is-soon' : 'is-open') : 'is-shut';

        return `
          <li>
            <button type="button" class="store" data-store-id="${esc(store.id)}">
              <span class="store__badge" aria-hidden="true">${esc(store.label)}</span>
              <span class="store__text">
                <span class="store__name">${esc(store.name)}</span>
                <span class="store__meta">${esc(place || store.address || '')}</span>
                <span class="store__status ${tone}">${esc(state.label)}</span>
              </span>
              ${store.distance != null
                ? `<span class="store__distance">${store.distance.toFixed(1)}<abbr title="kilometres">km</abbr></span>`
                : ''}
            </button>
          </li>`;
      }).join('');

      this.emptyEl.hidden = this.visible.length > 0;
      this.countEl.textContent = this.visible.length
        ? `${this.visible.length} store${this.visible.length === 1 ? '' : 's'}`
        : '';

      if (this.activeId) {
        const row = this.listEl.querySelector(`[data-store-id="${CSS.escape(this.activeId)}"]`);
        if (row) row.classList.add('is-active');
      }
    }

    relabelMarkers() {
      this.markers.forEach((marker, id) => {
        const store = this.stores.find((s) => s.id === id);
        const text = marker.content.querySelector('.pin__label');
        if (store && text) text.textContent = store.label;
      });
    }

    filter(term) {
      const query = term.trim().toLowerCase();
      this.visible = query
        ? this.stores.filter((store) =>
            [store.name, store.suburb, store.postcode, store.address]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
              .includes(query))
        : this.stores.slice();

      this.renderList();
      this.syncMarkers();
      this.fit();
    }

    locate(button) {
      if (!navigator.geolocation) return;
      button.setAttribute('aria-busy', 'true');

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const me = { lat: coords.latitude, lng: coords.longitude };
          this.stores.forEach((store) => { store.distance = distanceKm(me, store); });
          this.stores.sort((a, b) => a.distance - b.distance);
          this.visible.sort((a, b) => a.distance - b.distance);
          this.label();
          this.relabelMarkers();
          this.renderList();
          if (this.map) {
            this.map.setCenter(me);
            this.map.setZoom(11);
          }
          button.removeAttribute('aria-busy');
        },
        () => {
          button.removeAttribute('aria-busy');
          this.countEl.textContent = 'Location unavailable — search by suburb instead.';
        },
        { timeout: 8000 }
      );
    }

    /* Map ------------------------------------------------------------------ */

    async initMap() {
      await window.googleMapsReady;
      const { Map } = await google.maps.importLibrary('maps');
      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

      this.map = new Map(this.querySelector('[data-locator-map]'), {
        mapId: this.dataset.mapId,
        zoom: Number(this.dataset.zoom) || 9,
        center: { lat: this.stores[0].lat, lng: this.stores[0].lng },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'cooperative'
      });

      this.classList.add('is-map-ready');

      this.stores.forEach((store) => {
        const pin = document.createElement('div');
        pin.className = 'pin';
        pin.innerHTML = pinMarkup(store.label);

        const marker = new AdvancedMarkerElement({
          map: this.map,
          position: { lat: store.lat, lng: store.lng },
          content: pin,
          title: store.name
        });

        marker.addListener('click', () => this.select(store.id, { pan: true }));
        this.markers.set(store.id, marker);
      });

      this.fit();
    }

    syncMarkers() {
      if (!this.map) return;
      const ids = new Set(this.visible.map((s) => s.id));
      this.markers.forEach((marker, id) => { marker.map = ids.has(id) ? this.map : null; });
    }

    fit() {
      if (!this.map || !this.visible.length) return;
      const bounds = new google.maps.LatLngBounds();
      this.visible.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
      this.map.fitBounds(bounds, 72);
    }

    /* Selection ------------------------------------------------------------ */

    select(id, options = {}) {
      const store = this.stores.find((s) => s.id === id);
      if (!store) return;

      this.activeId = id;
      this.classList.add('is-detail-open');

      this.querySelectorAll('.store').forEach((el) => {
        el.classList.toggle('is-active', el.dataset.storeId === id);
      });
      this.markers.forEach((marker, key) => {
        marker.content.classList.toggle('is-active', key === id);
        marker.zIndex = key === id ? 10 : 1;
      });

      const row = this.listEl.querySelector(`[data-store-id="${CSS.escape(id)}"]`);
      if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

      this.renderDetail(store);

      if (options.pan && this.map) {
        this.map.panTo({ lat: store.lat, lng: store.lng });
        if (this.map.getZoom() < 12) this.map.setZoom(13);
      }
    }

    close() {
      this.activeId = null;
      this.classList.remove('is-detail-open');
      this.detailEl.hidden = true;
      this.querySelectorAll('.store').forEach((el) => el.classList.remove('is-active'));
      this.markers.forEach((marker) => marker.content.classList.remove('is-active'));
      this.fit();
    }

    renderDetail(store) {
      const today = nowIn(this.timezone).day;
      const state = status(store, this.timezone);
      const tone = state.open ? (state.soon ? 'is-soon' : 'is-open') : 'is-shut';
      const destination = encodeURIComponent(
        `${store.address || ''} ${store.suburb || ''} ${store.postcode || ''}`.trim()
      );

      const rows = DAYS.map((day, index) => `
        <tr class="${index === today ? 'is-today' : ''}">
          <th scope="row">${LABELS[index]}</th>
          <td>${hoursText(store.hours[day])}</td>
        </tr>`).join('');

      const services = (store.services || '').split(',').map((s) => s.trim()).filter(Boolean);

      this.detailEl.innerHTML = `
        <header class="detail__head">
          <button type="button" class="detail__close" data-detail-close aria-label="Close store details">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" fill="none"/></svg>
          </button>
          <span class="detail__badge" aria-hidden="true">${esc(store.label)}</span>
          <h3 class="detail__name">${store.url
            ? `<a href="${esc(store.url)}">${esc(store.name)}</a>`
            : esc(store.name)}</h3>
          <p class="detail__address">${esc([store.address, store.suburb, store.postcode].filter(Boolean).join(', '))}</p>
          <span class="store__status on-dark ${tone}">${esc(state.label)}</span>
        </header>

        <div class="detail__body">
          <div class="detail__actions">
            <a class="is-primary" href="https://www.google.com/maps/dir/?api=1&destination=${destination}" target="_blank" rel="noopener">Get directions</a>
            ${store.phone ? `<a class="is-secondary" href="tel:${esc(store.phone.replace(/\s/g, ''))}">Call store</a>` : ''}
          </div>

          ${store.image ? `<img class="detail__image" src="${esc(store.image)}" alt="" loading="lazy">` : ''}

          <h4 class="detail__heading">Opening hours</h4>
          <table class="hours"><tbody>${rows}</tbody></table>

          ${services.length ? `
            <h4 class="detail__heading">In this store</h4>
            <ul class="detail__services">${services.map((s) => `<li>${esc(s)}</li>`).join('')}</ul>
          ` : ''}
        </div>`;

      this.detailEl.hidden = false;
      this.detailEl.scrollTop = 0;
    }
  }

  customElements.define('store-locator', StoreLocator);
})();
